import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import LoginLink from "@/pages/LoginLink";
import AcceptInvite from "@/pages/AcceptInvite";
import StampClock from "@/pages/StampClock";
import Dashboard from "@/pages/Dashboard";
import Reports from "@/pages/Reports";
import Objects from "@/pages/Objects";
import Tasks from "@/pages/Tasks";
import Employees from "@/pages/Employees";
import Settings from "@/pages/Settings";
import Integrations from "@/pages/Integrations";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import Pricing from "@/pages/Pricing";
import PublicIntegrations from "@/pages/PublicIntegrations";
import AdminInquiries from "@/pages/AdminInquiries";
import Welcome from "@/pages/Welcome";
import TrashPage from "@/pages/Trash";
import DriveLog from "@/pages/DriveLog";
import IndustryLanding from "@/pages/IndustryLanding";
import AtaSigningPage from "@/pages/AtaSigningPage";
import Changelog from "@/pages/Changelog";
import Privacy from "@/pages/Privacy";
import DataDeletion from "@/pages/DataDeletion";
import TratrapporSkane from "@/pages/TratrapporSkane";
import WorkdayAdmin from "@/pages/WorkdayAdmin";
import Lonerapport from "@/pages/Lonerapport";
import Attest from "@/pages/Attest";
import MyMonth from "@/pages/MyMonth";
import CookieBanner from "@/components/CookieBanner";

function Shell({ children, adminOnly = false }) {
  return (
    <ProtectedRoute adminOnly={adminOnly}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

/**
 * Rot-rutten `/` visar landningssidan för icke-inloggade besökare
 * (publik marknadsföring) och stämpelklockan för inloggade användare.
 * Under `loading` väntar vi tyst — landningssidans hero är stor och skulle
 * blinka annars.
 */
function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <p className="text-text-muted">Laddar…</p>
      </div>
    );
  }
  if (!user) return <Landing />;
  return <Shell><StampClock /></Shell>;
}

/**
 * Catch-all som fångar /tidrapportering-<slug> och renderar IndustryLanding
 * med rätt slug. RR v7 stödjer inte "prefix + :param" i samma segment, så vi
 * matchar manuellt här. Alla andra okända paths skickas till startsidan.
 */
function IndustryRouteMatcher() {
  const location = useLocation();
  const match = location.pathname.match(/^\/tidrapportering-([a-z0-9-]+)\/?$/i);
  if (match) {
    return <IndustryLanding key={match[1]} slugOverride={match[1].toLowerCase()} />;
  }
  return <Navigate to="/" replace />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrandingProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <CookieBanner />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/login-link" element={<LoginLink />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/integrationer" element={<PublicIntegrations />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/tratrappor-skane" element={<TratrapporSkane />} />
            {/* Industry landing pages: /tidrapportering-<slug>. React Router v7
                stödjer inte "prefix + :param" i samma URL-segment, så vi fångar
                dessa via en catch-all i slutet av <Routes>. Se IndustryRouteMatcher. */}
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/ata-signering/:token" element={<AtaSigningPage />} />
            {/* Billing-sidorna kringgår subscription-guarden så användaren
                alltid kan komma åt att åtgärda betalning eller stänga av */}
            <Route path="/billing" element={<Billing />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/billing/cancel" element={<BillingCancel />} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/welcome" element={<Shell adminOnly><Welcome /></Shell>} />
            <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
            <Route path="/reports" element={<Shell adminOnly><Reports /></Shell>} />
            <Route path="/objects" element={<Shell><Objects /></Shell>} />
            <Route path="/tasks" element={<Shell adminOnly><Tasks /></Shell>} />
            <Route path="/employees" element={<Shell adminOnly><Employees /></Shell>} />
            <Route path="/integrations" element={<Shell adminOnly><Integrations /></Shell>} />
            <Route path="/settings" element={<Shell><Settings /></Shell>} />
            <Route path="/admin/inquiries" element={<Shell adminOnly><AdminInquiries /></Shell>} />
            <Route path="/papperskorg" element={<Shell adminOnly><TrashPage /></Shell>} />
            <Route path="/versionslogg" element={<Shell adminOnly><Changelog /></Shell>} />
            <Route path="/korjournal" element={<Shell><DriveLog /></Shell>} />
            <Route path="/loneinstallningar" element={<Shell adminOnly><WorkdayAdmin /></Shell>} />
            <Route path="/lonerapport" element={<Shell adminOnly><Lonerapport /></Shell>} />
            <Route path="/attest" element={<Shell adminOnly><Attest /></Shell>} />
            <Route path="/mina-timmar" element={<Shell><MyMonth /></Shell>} />
            <Route path="*" element={<IndustryRouteMatcher />} />
          </Routes>
        </BrowserRouter>
        </BrandingProvider>
      </AuthProvider>
    </div>
  );
}
